import { Component, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { AlertService } from 'src/app/components/_alert/alert.service';
import { ESystemAccess, ESystemProfileList } from 'src/app/enum/EAccess';
import { SessionService } from 'src/app/shared/services/session.service';
import { AdministracionCarteraApiService } from '../services/administracion-cartera-api.service';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { BagResponse } from '../models/bag';
import { BatchConfigListItem, Frequency, Mapper, OrgBatchTemplate } from '../models/batch';
import { QueryDetailModalComponent } from '../components/query-detail-modal/query-detail-modal.component';
import { MatStepper } from '@angular/material/stepper';
import { TemplateDetailModalComponent } from '../components/template-detail-modal/template-detail-modal.component';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatConfirmDialogComponent } from 'src/app/components/mat-confirm-dialog/mat-confirm-dialog.component';
import { takeUntil } from 'rxjs/operators';
import { goToTop } from 'src/app/shared/utils/utils';

@Component({
    selector: 'app-admin-cartera-process-panel',
    templateUrl: './admin-cartera-process-panel.component.html',
    styleUrls: ['./admin-cartera-process-panel.component.scss']
})
export class AdminCarteraProcessPanelComponent {
    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatSort) sort!: MatSort;
    @ViewChild('stepper') private stepper!: MatStepper;

    private access: string[] = [
        ESystemAccess.ADMINISTRACION_CARTERA_ADMIN,
        ESystemProfileList.WEB
    ];

    private destroy$ = new Subject<void>();

    private id: string | null = null;
    private batchConfigs: BatchConfigListItem[] = []
    private mappersContract: Mapper[] = []

    public detail: BagResponse | null = null;

    public modelos: OrgBatchTemplate[] = []
    public modeloSelected: OrgBatchTemplate | undefined = undefined
    public modeloControl = new FormControl('', Validators.required);

    public frecuencias: Frequency[] = []
    public frecuenciaSelected: Frequency | undefined = undefined
    public frecuenciaControl = new FormControl('', Validators.required);

    public queries: Mapper[] = []
    public querySelected: Mapper | undefined = undefined
    public queryControl = new FormControl('', Validators.required);

    public isAsigning = true;

    public stepperIndex = 0;

    public dataSource = new MatTableDataSource<BatchConfigListItem>();
    public displayedColumns = ['modelo', 'frecuencia', 'destino', 'acciones'];

    public isBatchConfigListLoading = false;
    public isModelosLoading = false;
    public isMappersLoading = false;

    public processAssignForm = this.fb.group({
        modelo: this.fb.control<OrgBatchTemplate | null>(null, Validators.required),
        frecuencia: this.fb.control<Frequency | null>(null, Validators.required),
        query: this.fb.control<Mapper | null>(null, Validators.required),
    });

    constructor(
        private _alertsService: AlertService,
        private _apiService: AdministracionCarteraApiService,
        private _router: Router,
        private _session: SessionService,
        private _route: ActivatedRoute,
        private fb: FormBuilder,
        public dialog: MatDialog,
    ) { }

    ngOnInit(): void {
        if (!this.checkAccess()) {
            this._router.navigate(['/request-access'])
            return;
        }

        this._route.paramMap.subscribe(params => {
            this.id = params.get('id');

            if (this.id) {
                this.getBagDetail(this.id);
                this.getBatchConfigs(this.id);
                this.getOrgBatchTemplates();
                this.getOrgMappers();
            }
        });

        const modeloControl = this.processAssignForm.get('modelo');

        modeloControl?.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe((value) => {
                if (!value) return;

                this.setFrequencies();
                this.setQueries();

                this.processAssignForm.patchValue({
                    frecuencia: null,
                    query: null
                });
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    getBagDetail(id: string) {
        this._apiService.getBagDetail(id).subscribe({
            next: (res) => {
                this.detail = res;
            },
            error: () => {
                this._alertsService.error("Ocurrió un error al intentar obtener el detalle de la cartera");
                goToTop();
            }
        });
    }

    getBatchConfigs(id: string) {
        this.isBatchConfigListLoading = true;

        this._apiService.getBatchConfigs(id).subscribe({
            next: (res) => {
                this.batchConfigs = res.batchConfigs || [];
                this.updateDataSource();
                this.isBatchConfigListLoading = false;
            },
            error: () => {
                this._alertsService.error("Ocurrió un error al intentar obtener los procesos asignados a la cartera");
                this.isBatchConfigListLoading = false;
                goToTop();
            }
        });
    }

    getOrgBatchTemplates() {
        this.isModelosLoading = true;
        this._apiService.listOrgBatchTemplates().subscribe({
            next: (res) => {
                this.modelos = res.batchTemplates || [];
                this.isModelosLoading = false;
            },
            error:() => {
                this._alertsService.error("Ocurrió un error al intentar obtener los modelos de la cartera.");
                this.isModelosLoading = false;
                goToTop();
            }
        });
    }

    getOrgMappers() {
        this.isMappersLoading = true;

        this._apiService.listOrgMappers().subscribe({
            next: (res) => {
                this.mappersContract = res.mappers || [];
                this.setQueries();

                this.isMappersLoading = false;
            },
            error:() => {
                this._alertsService.error("Ocurrió un error al intentar obtener los mappers de la cartera.");
                this.isMappersLoading = false;
                goToTop();
            }
        });
    }

    checkAccess() {
        const hasAdminCarteraPermission = this._session.getUserAccess().includes(this.access[0]);
        const hasWebProfile = this._session.getUserProfileList().includes(this.access[1]);

        return hasAdminCarteraPermission && hasWebProfile;
    }

    onEdit() {
        this._router.navigate(['/administracion-cartera-editar', this.id]);
    }

    goBack() {
        this._router.navigate(['/administracion-cartera-detalle', this.id]);
    }

    setFrequencies() {
        const modeloControl = this.processAssignForm.get('modelo');
        this.frecuencias = modeloControl?.value?.batchTemplate.availableFrequencies || [];
    }

    setQueries() {
        const modeloControl = this.processAssignForm.get('modelo');
        const mappersModelo = modeloControl?.value?.batchTemplate.mappers || [];

        if (!mappersModelo?.length || !this.mappersContract?.length) {
            this.queries = [];
            return;
        }

        this.queries = this.mapIdWithNames(mappersModelo, this.mappersContract);

        if (this.queries.length > 0) {
            this.querySelected = this.queries[0];
        }
    }


    asignConfig() {
        this.isAsigning = true;

        const frecuencia = this.processAssignForm.get('frecuencia')?.value;
        const modelo = this.processAssignForm.get('modelo')?.value;
        const query = this.processAssignForm.get('query')?.value;

        if (!frecuencia || !modelo || !query) {
            this.isAsigning = false;
            return
        }

        this._apiService.asignBatchConfig(this.id!, frecuencia, modelo, query).subscribe({
            next: () => {
                this.isAsigning = false;
                this.getBatchConfigs(this.id!);

                this.processAssignForm.reset();
                this.stepperIndex = 0;

                this._alertsService.success("Configuración asignada correctamente.");
            },
            error: () => {
                this._alertsService.error("Ocurrió un error al intentar asignar la configuración.");
                this.isAsigning = false;
                goToTop();
            }
        });
    }

    removeConfig(process: BatchConfigListItem) {
        const dialogRef = this.dialog.open(MatConfirmDialogComponent, {
            data: {
                title: 'Confirmación',
                message: `¿Esta seguro que desea eliminar la cartera ${process.id}?`
            }
        });

        dialogRef.afterClosed().subscribe(resp => {
            if (!resp) return;

            this._apiService.removeBatchConfig(process.id, this.id!).subscribe({
                next: () => {
                    this._alertsService.success('Configuración eliminada exitosamente.');
                    this.getBatchConfigs(this.id!);
                },
                error: () => {
                    this._alertsService.error('Ocurrió un error al intentar eliminar la configuración.');
                    goToTop();
                }}
            )
        });
    }

    restartStepper() {
        this.stepper.reset();
    }

    mapIdWithNames(idsArray: Mapper[], namesArray: Mapper[]): Mapper[] {
        return idsArray
            .map((idObj: Mapper) => {
                // Find the object in namesArray that matches the id
                const matchingNameObj = namesArray.find(nameObj => nameObj.id === idObj.id);

                // Return a new object with id and name, or undefined if no match is found
                return matchingNameObj ? { id: idObj.id, name: matchingNameObj.name } as Mapper : undefined;
            })
            .filter((obj): obj is Mapper => obj !== undefined);
    }

    openQueryDetail(queryId: string) {
        this.dialog.open(QueryDetailModalComponent, {
            data: {
                id: queryId
            }
        });
    }

    openTemplateDetail(model: OrgBatchTemplate) {
        this.dialog.open(TemplateDetailModalComponent, {
            data: model.batchTemplate
        });
    }

    updateDataSource() {
        setTimeout(() => {
            this.dataSource = new MatTableDataSource<BatchConfigListItem>(this.batchConfigs);
            this.dataSource.paginator = this.paginator;
            this.dataSource.sort = this.sort;
        }, 0);
    }

}
